import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { toast } from 'sonner';
import { Check, CreditCard, Calendar as CalendarIcon, Clock, User, Music, Mic2, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Booking = () => {
  const [date, setDate] = useState();
  const [service, setService] = useState('');
  const [timeSlot, setTimeSlot] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [loading, setLoading] = useState(false);
  const [occupiedSlots, setOccupiedSlots] = useState([]);

  // Specific options
  const [recordingOption, setRecordingOption] = useState(2);
  const [mixingType, setMixingType] = useState('remote');
  const [engineer, setEngineer] = useState('Tristan'); // Default

  const recordingPackages = [
    { label: '2 Heures', value: 2, price: 80 },
    { label: '3 Heures', value: 3, price: 120 },
    { label: 'Demi-journée (4h)', value: 4, price: 150 },
    { label: 'Journée (8h)', value: 8, price: 280 },
  ];

  const services = [
    { id: 'recording', name: 'Enregistrement', basePrice: 40 },
    { id: 'mixing', name: 'Mixage', basePrice: 100 },
    { id: 'mastering', name: 'Mastering', basePrice: 50 }
  ];

  const timeSlots = ['10:00', '12:00', '14:00', '16:00', '18:00', '20:00'];

  // Helper: Is this a remote service?
  const isRemote = service === 'mixing' && mixingType === 'remote';
  // Helper: Does this service require an engineer?
  const needsEngineer = service === 'recording' || (service === 'mixing' && mixingType === 'onsite');

  // Fetch availability when date changes
  useEffect(() => {
    if (date) {
        const fetchAvailability = async () => {
            try {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const dateStr = `${year}-${month}-${day}`;
                
                const res = await axios.get(`${BACKEND_URL}/api/availability?date=${dateStr}`);
                setOccupiedSlots(res.data.occupied);
            } catch (e) {
                console.error("Availability error", e);
            }
        };
        fetchAvailability();
    } else {
        setOccupiedSlots([]);
    }
  }, [date]);

  const getFinalPrice = () => {
    if (service === 'recording') {
      const pkg = recordingPackages.find(p => p.value === recordingOption);
      return pkg ? pkg.price : 80;
    }
    if (service === 'mixing') return 100;
    if (service === 'mastering') return 50;
    return 0;
  };

  const getServiceLabel = () => {
    let label = '';
    if (service === 'recording') {
      const pkg = recordingPackages.find(p => p.value === recordingOption);
      label = `Enregistrement (${pkg?.label})`;
    } else if (service === 'mixing') {
      label = `Mixage (${mixingType === 'onsite' ? 'Présentiel' : 'À distance'})`;
    } else if (service === 'mastering') {
      label = 'Mastering';
    }

    if (needsEngineer) {
      label += ` avec ${engineer}`;
    }
    return label;
  };

  const handleBook = async () => {
    if (!service || !formData.name || !formData.email) {
       toast.error("Veuillez remplir vos coordonnées et choisir un service");
       return;
    }
    
    if (!isRemote && (!date || !timeSlot)) {
       toast.error("Veuillez choisir une date et une heure");
       return;
    }

    setLoading(true);
    const finalPrice = getFinalPrice();
    const finalServiceLabel = getServiceLabel();
    const finalDate = date ? date.toISOString() : null;
    
    try {
      await axios.post(`${BACKEND_URL}/api/bookings`, {
          client_name: formData.name,
          client_email: formData.email,
          service_type: finalServiceLabel,
          date: finalDate,
          time_slot: timeSlot,
          status: 'confirmed', // Directly confirmed for pay on site? Or pending? Pending is safer.
          payment_status: 'pay_on_site',
          amount: finalPrice,
          engineer: needsEngineer ? engineer : null,
          is_remote: isRemote
      });
      
      toast.success("Réservation confirmée ! Vous recevrez un email.");
      setService('');
      setTimeSlot('');
      
      // Instant availability update
      setOccupiedSlots(prev => [...prev, timeSlot]);
      setTimeout(() => {
          if(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            axios.get(`${BACKEND_URL}/api/availability?date=${year}-${month}-${day}`)
                 .then(res => setOccupiedSlots(res.data.occupied));
          }
      }, 500);
      
      setDate(undefined);
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de la réservation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-24 pb-20 px-4 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-bold mb-8 text-center bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-blue-400">
          Réserver votre Session
        </h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column */}
          <div className="space-y-6">
             {/* Service Selection */}
             <div className="bg-neutral-900/50 backdrop-blur-md p-6 rounded-2xl border border-white/10 shadow-xl">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                 <Music className="text-purple-400" /> 1. Choisissez votre service
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                {services.map(s => (
                  <button 
                    key={s.id}
                    onClick={() => setService(s.id)}
                    className={`p-4 rounded-xl border text-left transition-all relative overflow-hidden group ${
                      service === s.id 
                        ? 'bg-purple-600 border-purple-500 text-white shadow-lg shadow-purple-900/20' 
                        : 'bg-black/40 border-white/10 hover:bg-white/5 text-gray-300'
                    }`}
                  >
                    <div className="font-bold relative z-10">{s.name}</div>
                    <div className={`text-xs mt-1 relative z-10 ${service === s.id ? 'text-white/80' : 'text-gray-500'}`}>
                        {s.id === 'recording' ? 'À partir de 80€' : `${s.basePrice}€`}
                    </div>
                  </button>
                ))}
              </div>

              {/* Dynamic Options */}
              {service === 'recording' && (
                <div className="mt-4 p-4 bg-black/20 rounded-xl border border-white/5 animate-in fade-in slide-in-from-top-2">
                    <label className="text-sm text-gray-400 mb-3 block">Durée de la session :</label>
                    <div className="grid grid-cols-2 gap-3">
                        {recordingPackages.map(pkg => (
                            <button
                                key={pkg.value}
                                onClick={() => setRecordingOption(pkg.value)}
                                className={`p-3 rounded-lg border text-sm flex justify-between items-center transition-all ${
                                    recordingOption === pkg.value
                                        ? 'bg-purple-500/20 border-purple-500 text-white'
                                        : 'bg-black/40 border-white/10 hover:bg-white/5 text-gray-400'
                                }`}
                            >
                                <span>{pkg.label}</span>
                                <span className="font-bold">{pkg.price}€</span>
                            </button>
                        ))}
                    </div>
                </div>
              )}

              {service === 'mixing' && (
                <div className="mt-4 p-4 bg-black/20 rounded-xl border border-white/5 animate-in fade-in slide-in-from-top-2">
                    <label className="text-sm text-gray-400 mb-3 block">Type de session :</label>
                    <div className="flex gap-4">
                        <button
                            onClick={() => setMixingType('remote')}
                            className={`flex-1 p-3 rounded-lg border text-sm transition-all ${
                                mixingType === 'remote'
                                    ? 'bg-blue-500/20 border-blue-500 text-white'
                                    : 'bg-black/40 border-white/10 hover:bg-white/5 text-gray-400'
                            }`}
                        >
                            À distance
                        </button>
                        <button
                            onClick={() => setMixingType('onsite')}
                            className={`flex-1 p-3 rounded-lg border text-sm transition-all ${
                                mixingType === 'onsite'
                                    ? 'bg-blue-500/20 border-blue-500 text-white'
                                    : 'bg-black/40 border-white/10 hover:bg-white/5 text-gray-400'
                            }`}
                        >
                            En présentiel
                        </button>
                    </div>
                </div>
              )}

              {/* Engineer Selection */}
              {needsEngineer && (
                 <div className="mt-4 p-4 bg-black/20 rounded-xl border border-white/5 animate-in fade-in slide-in-from-top-2">
                    <label className="text-sm text-gray-400 mb-3 flex items-center gap-2">
                       <Users className="w-4 h-4" /> Choix de l'ingénieur son :
                    </label>
                    <div className="flex gap-4">
                        <button
                            onClick={() => setEngineer('Tristan')}
                            className={`flex-1 p-3 rounded-lg border text-sm transition-all ${
                                engineer === 'Tristan'
                                    ? 'bg-purple-500/20 border-purple-500 text-white'
                                    : 'bg-black/40 border-white/10 hover:bg-white/5 text-gray-400'
                            }`}
                        >
                            Tristan
                        </button>
                        <button
                            onClick={() => setEngineer('Gregor')}
                            className={`flex-1 p-3 rounded-lg border text-sm transition-all ${
                                engineer === 'Gregor'
                                    ? 'bg-purple-500/20 border-purple-500 text-white'
                                    : 'bg-black/40 border-white/10 hover:bg-white/5 text-gray-400'
                            }`}
                        >
                            Gregor
                        </button>
                    </div>
                 </div>
              )}
            </div>

            {/* Calendar / Remote Info */}
            <div className="bg-neutral-900/50 backdrop-blur-md p-6 rounded-2xl border border-white/10 shadow-xl">
              {isRemote ? (
                  <div className="text-center py-8">
                      <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4 text-blue-400">
                          <Clock className="w-8 h-8" />
                      </div>
                      <h3 className="text-xl font-bold mb-2">Pas de rendez-vous nécessaire</h3>
                      <p className="text-gray-300 max-w-sm mx-auto">
                          Le mixage sera disponible dans votre espace utilisateur d'ici <span className="text-purple-400 font-bold">10 jours ouvrés</span>. 
                          Un lien vous sera envoyé par email dès qu'il sera prêt.
                      </p>
                  </div>
              ) : (
                  <>
                    <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                        <CalendarIcon className="text-blue-400" /> 2. Date & Heure
                    </h2>
                    <div className="flex flex-col md:flex-row gap-8 items-center md:items-start justify-center">
                        <div className="booking-calendar-wrapper">
                            <DayPicker
                                mode="single"
                                selected={date}
                                onSelect={setDate}
                                disabled={{ before: new Date() }}
                                modifiersClassNames={{
                                    selected: 'rdp-day_selected',
                                    disabled: 'rdp-day_disabled'
                                }}
                                styles={{
                                    caption: { color: 'white' },
                                    head_cell: { color: 'gray' },
                                    day: { color: 'white' }
                                }}
                            />
                        </div>
                        <div className="w-full md:w-auto">
                            <div className="grid grid-cols-3 md:grid-cols-2 gap-3">
                                {timeSlots.map(time => {
                                  const isOccupied = occupiedSlots.includes(time);
                                  return (
                                    <button
                                        key={time}
                                        disabled={isOccupied}
                                        onClick={() => setTimeSlot(time)}
                                        className={`p-2 px-4 rounded-lg border transition-all text-sm ${
                                        isOccupied 
                                            ? 'bg-red-900/20 border-red-900/50 text-red-700 cursor-not-allowed opacity-50'
                                            : timeSlot === time 
                                                ? 'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-900/20' 
                                                : 'bg-black/40 border-white/10 hover:bg-white/5 text-gray-300'
                                        }`}
                                    >
                                        {time}
                                    </button>
                                  );
                                })}
                            </div>
                        </div>
                    </div>
                  </>
              )}
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
             {/* User Details */}
            <div className="bg-neutral-900/50 backdrop-blur-md p-6 rounded-2xl border border-white/10 shadow-xl">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <User className="text-pink-400" /> 3. Vos coordonnées
              </h2>
              <div className="space-y-4">
                <input 
                  type="text" 
                  placeholder="Nom complet" 
                  className="w-full bg-black/50 border border-white/10 rounded-xl p-4 focus:outline-none focus:border-purple-500 transition-colors"
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                />
                <input 
                  type="email" 
                  placeholder="Email" 
                  className="w-full bg-black/50 border border-white/10 rounded-xl p-4 focus:outline-none focus:border-purple-500 transition-colors"
                  value={formData.email}
                  onChange={e => setFormData({...formData, email: e.target.value})}
                />
              </div>
            </div>

            {/* Summary */}
            <div className="bg-neutral-900/50 backdrop-blur-md p-6 rounded-2xl border border-white/10 shadow-xl">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <Check className="text-green-400" /> 4. Récapitulatif
                </h2>
                
                <div className="bg-black/40 rounded-xl p-4 mb-6 border border-white/5 space-y-2">
                    <div className="flex justify-between text-gray-400">
                        <span>Service</span>
                        <span className="text-white font-medium text-right text-sm">{service ? getServiceLabel() : '-'}</span>
                    </div>
                    {!isRemote && (
                        <div className="flex justify-between text-gray-400">
                            <span>Date</span>
                            <span className="text-white font-medium">
                                {date ? date.toLocaleDateString('fr-FR') : '-'} à {timeSlot || '-'}
                            </span>
                        </div>
                    )}
                    {isRemote && (
                        <div className="flex justify-between text-gray-400">
                            <span>Délai</span>
                            <span className="text-white font-medium">10 jours ouvrés</span>
                        </div>
                    )}
                    <div className="h-px bg-white/10 my-2" />
                    <div className="flex justify-between text-lg font-bold">
                        <span>Total à payer sur place</span>
                        <span className="text-purple-400">{getFinalPrice()}€</span>
                    </div>
                </div>

                <button 
                    onClick={handleBook}
                    disabled={loading || (!isRemote && !timeSlot)}
                    className="w-full py-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl font-bold text-lg hover:opacity-90 transition-all disabled:opacity-50 shadow-lg shadow-purple-900/20 flex items-center justify-center gap-2"
                >
                    {loading ? 'Traitement...' : 'Confirmer la réservation'}
                </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Booking;
